import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FormModal, Input, useToastActions } from '@airclou/ui';
import { createWorkspace, type CreateWorkspaceInput } from '@/api/workspaces';

interface CreateWorkspaceModalProps {
  companyId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function CreateWorkspaceModal({ companyId, isOpen, onClose, onSuccess }: CreateWorkspaceModalProps) {
  const { t } = useTranslation('dashboard');
  const { success, error } = useToastActions();
  const [loading, setLoading] = useState(false);
  // The plan's workspace cap (brand-and-mailbox-caps-plan.md §4): said inline,
  // with the upgrade path, rather than as a toast.
  const [limitMessage, setLimitMessage] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateWorkspaceInput>({
    name: 'Default Workspace',
    slug: 'default-workspace',
    company_id: companyId,
    metadata: {},
  });

  const handleSubmit = async () => {
    setLoading(true);
    setLimitMessage(null);

    try {
      await createWorkspace(formData);
      success(t('workspaces.workspaceCreated'), t('common.success'));
      setFormData({ name: '', slug: '', company_id: companyId, metadata: {} });
      onSuccess();
      onClose();
    } catch (err: any) {
      if (err?.code === 'WORKSPACE_LIMIT_REACHED') setLimitMessage(t('workspaces.limitReached'));
      else error(err.message || t('workspaces.failedToCreate'), t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleNameChange = (value: string) => {
    const slug = value
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .substring(0, 100);
    
    setFormData({ ...formData, name: value, slug });
  };

  const canSubmit = formData.name.trim().length > 0 && formData.slug.trim().length > 0;

  const close = () => {
    setLimitMessage(null);
    onClose();
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={close}
      title={t('workspaces.createWorkspace')}
      size="md"
      submitText={loading ? t('common.creating') : t('workspaces.createWorkspace')}
      canSubmit={canSubmit && !loading}
      onSubmit={handleSubmit}
    >
      <div className="space-y-6">
        {limitMessage && (
          <div role="alert" className="rounded-lg border border-border bg-muted/50 p-3 text-sm text-foreground">
            <p>{limitMessage}</p>
            <Link to="/billing/subscription" className="mt-1 inline-block font-medium text-primary" onClick={close}>
              {t('workspaces.seePlans')}
            </Link>
          </div>
        )}
        <div>
          <label htmlFor="workspace-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('workspaces.workspaceName')}
          </label>
          <Input
            id="workspace-name"
            type="text"
            value={formData.name}
            onChange={handleNameChange}
            placeholder="e.g., Backend Team"
          />
        </div>

        <div>
          <label htmlFor="workspace-slug" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('common.slug')}
          </label>
          <Input
            id="workspace-slug"
            type="text"
            value={formData.slug}
            onChange={(value) => setFormData({ ...formData, slug: value })}
            placeholder="e.g., backend-team"
          />
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {t('common.slugHint')}
          </p>
        </div>
      </div>
    </FormModal>
  );
}
